import { useEffect, useState } from "react";
import {
  footerInkForBg,
  footerSampleStripHeight,
} from "@shared/email-footer-sampling";

// Editor-side mirror of the send path's bottom-edge footer sampling
// (server/services/email-mjml.ts sampleImageBottomColor): the polished /
// captured preview stacks close with the live-text footer, and the sent
// stacked-image email paints that footer with the average color of the bottom
// ~2% of the LAST block image. This hook samples the same image client-side
// (canvas) so the preview footer matches the sent one.
//
// R2 / remote images are routed through the same-origin /api/img-proxy so the
// canvas isn't CORS-tainted (direct R2 reads would throw on getImageData).
// The proxy downscales to `w` with aspect preserved, which keeps the bottom
// 2% strip's average color intact.
//
// Best-effort by design: any failure (load error, tainted canvas, decode)
// resolves to null and the footer keeps the brand style's page background —
// the SAME fallback order as the send path.
export function useSampledFooterStyle(
  url: string | null | undefined,
): { bg: string; text: string } | null {
  const [style, setStyle] = useState<{ bg: string; text: string } | null>(null);

  useEffect(() => {
    setStyle(null);
    if (!url) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return;
        const stripH = footerSampleStripHeight(h);
        const canvas = document.createElement("canvas");
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;
        // Draw the bottom strip into a single pixel — the browser's downscale
        // averages it, mirroring sharp's resize(1,1,{fit:"fill"}) on the server.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, h - stripH, w, stripH, 0, 0, 1, 1);
        const d = ctx.getImageData(0, 0, 1, 1).data;
        const toHex = (n: number) => n.toString(16).padStart(2, "0");
        const bg = `#${toHex(d[0])}${toHex(d[1])}${toHex(d[2])}`;
        setStyle({ bg, text: footerInkForBg(bg) });
      } catch {
        // Tainted canvas / decode failure — keep the brand-style fallback.
      }
    };
    img.src = /^https?:\/\//i.test(url)
      ? `/api/img-proxy?url=${encodeURIComponent(url)}&w=600&q=85`
      : url;
    return () => {
      cancelled = true;
    };
  }, [url]);

  return style;
}
